/** EU regulatory submission pathways — the dropdown options shown when
 *  a user picks EU at dossier dispatch time. Mirrors the US file's shape
 *  so the dispatch form can plug it in with a country-key swap.
 *
 *  ## Devices — split across two regulations
 *
 *  - **MDR — Regulation (EU) 2017/745**: physical / surgical / implantable
 *    devices. Risk-based class system; higher classes pull in a Notified
 *    Body. Conformity-assessment modules come from MDR Annexes IX-XI.
 *
 *  - **IVDR — Regulation (EU) 2017/746**: in-vitro diagnostics.
 *    Replaced the old IVDD and introduced a similar risk-based class
 *    system; most IVDs now require Notified Body involvement.
 *
 *  All MDR / IVDR pathways end in CE marking + mandatory EUDAMED
 *  registration + UDI assignment.
 *
 *  ## Drugs + Biologics — Article-based (Directive 2001/83/EC)
 *
 *  Same Article framework applies to both, but with two
 *  category-specific exclusions:
 *
 *    - **Article 10(1) — generic**: DRUGS ONLY (small-molecule generic
 *      via bioequivalence).
 *    - **Article 10(4) — similar biological medicinal product (biosimilar)**:
 *      BIOLOGICS ONLY.
 *
 *  Articles 8(3), 10a, 10b, 10c, 10(3) apply to both families.
 *
 *  Abridged applications (10-series) can only be filed once the reference
 *  product's data-protection period (typically 8 years) has expired.
 */

import type { Product } from '@/features/product/types';
import type { SubmissionGroup } from './us-submission-pathways';

export const EU_SUBMISSION_PATHWAYS: SubmissionGroup[] = [
  {
    productType: 'device',
    label: 'Medical Device — MDR / IVDR',
    options: [
      // MDR (Regulation (EU) 2017/745) — physical devices.
      // Class I variants (I / Is / Im / Ir) are intentionally NOT offered: they
      // are self-declared / limited-NB and the dossier generator supports only
      // IIa / IIb / IIb_implantable / III (app/dossier/eu/config.SUPPORTED_CLASSES),
      // so offering them would only 400 at dispatch.
      { value: 'mdr-class-iia', label: 'MDR Class IIa — NB technical-doc sample + QMS audit' },
      { value: 'mdr-class-iib', label: 'MDR Class IIb — NB broader tech-doc review + QMS (per-device for implantables)' },
      { value: 'mdr-class-iii', label: 'MDR Class III — full QMS + tech-doc review (+ scrutiny)' },

      // IVDR (Regulation (EU) 2017/746) — in-vitro diagnostics
      { value: 'ivdr-class-a-sterile', label: 'IVDR Class A sterile — NB for sterility' },
      { value: 'ivdr-class-b',         label: 'IVDR Class B — NB QMS audit + tech-doc sample' },
      { value: 'ivdr-class-c',         label: 'IVDR Class C — NB QMS + tech-doc review' },
      { value: 'ivdr-class-d',         label: 'IVDR Class D — most stringent + EURL batch verification' },
    ],
  },
  {
    productType: 'drug',
    label: 'Drug — Directive 2001/83/EC',
    options: [
      // Full / stand-alone applications
      { value: 'art-8-3',  label: 'Article 8(3) — full / complete application' },
      { value: 'art-10a',  label: 'Article 10a — well-established use (bibliographic)' },
      { value: 'art-10b',  label: 'Article 10b — fixed combination (new combination of known substances)' },
      { value: 'art-10c',  label: 'Article 10c — informed consent (original MAH consents to cross-reference)' },
      // Abridged applications
      { value: 'art-10-1', label: 'Article 10(1) — generic (bioequivalence to reference product)' },
      { value: 'art-10-3', label: 'Article 10(3) — hybrid (partial reliance on reference + new data)' },
    ],
  },
  {
    productType: 'biologic',
    label: 'Biologic — Directive 2001/83/EC',
    options: [
      // Full / stand-alone applications
      { value: 'art-8-3',  label: 'Article 8(3) — full / complete application' },
      { value: 'art-10a',  label: 'Article 10a — well-established use (bibliographic)' },
      { value: 'art-10b',  label: 'Article 10b — fixed combination' },
      { value: 'art-10c',  label: 'Article 10c — informed consent' },
      // Abridged applications
      { value: 'art-10-3', label: 'Article 10(3) — hybrid' },
      { value: 'art-10-4', label: 'Article 10(4) — similar biological medicinal product (biosimilar)' },
    ],
  },
];

/** Every EU medicinal legal basis (Directive 2001/83/EC) — the drug + biologic
 *  options above, which are also the submission_type values a medicinal run
 *  stores. The one list: the summary-card eyebrow, the stepper copy and the
 *  dispatch legal-basis mapping all read it (a hand-kept 3-item copy once
 *  labeled Art. 10a/10b/10c/10(3) dossiers "FDA"). */
export const EU_MEDICINAL_LEGAL_BASES: ReadonlySet<string> = new Set(
  EU_SUBMISSION_PATHWAYS.filter(
    (g) => g.productType === 'drug' || g.productType === 'biologic',
  ).flatMap((g) => g.options.map((o) => o.value)),
);

/** Pre-fill the picker with the most-common pathway per product type so
 *  the form is dispatchable immediately on load. For EU devices the
 *  default is MDR Class IIa (the most common notified-body track for
 *  typical commercial devices); IVD applicants pick a Class A-D option
 *  manually. For drugs + biologics the default is Article 8(3) (full
 *  application) which is the path for any novel active substance. */
export const EU_DEFAULT_BY_TYPE: Record<Product['product_type'], string> = {
  device: 'mdr-class-iia',
  drug: 'art-8-3',
  biologic: 'art-8-3',
  combination: 'mdr-class-iia',
};
